import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Alert,
  IconButton,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { useMaterialUIController } from 'context';
import { useNavigate } from 'react-router-dom';

const BulkUploadDialog = ({ open, onClose, moduleName, onUploadSuccess }) => {
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [pollingJobId, setPollingJobId] = useState(null);

  const checkJobStatus = async (jobId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASEURL}bulk-upload/logs`,
        {
          withCredentials: true,
        }
      );

      if (response.data?.status && response.data?.data) {
        const job = response.data.data.find(log => log.jobId === jobId);
        
        if (job && job.status === 'COMPLETED') {
          if (job.failedCount > 0) {
            enqueueSnackbar(
              `Upload completed with ${job.successCount} success and ${job.failedCount} failures`,
              { 
                variant: 'warning',
                autoHideDuration: 6000,
                action: (key) => (
                  <Button 
                    size="small" 
                    style={{ color: '#fff' }}
                    onClick={() => {
                      navigate('/bulk-upload-logs');
                    }}
                  >
                    View Logs
                  </Button>
                ),
              }
            );
          } else {
            enqueueSnackbar(
              `Upload completed successfully! ${job.successCount} records uploaded.`,
              { 
                variant: 'success',
                autoHideDuration: 5000,
                action: (key) => (
                  <Button 
                    size="small" 
                    style={{ color: '#fff' }}
                    onClick={() => {
                      navigate('/bulk-upload-logs');
                    }}
                  >
                    View Logs
                  </Button>
                ),
              }
            );
          }
          setPollingJobId(null);
          return true;
        } else if (job && job.status === 'FAILED') {
          enqueueSnackbar(
            'Upload failed. Please check the logs for details.',
            { 
              variant: 'error',
              autoHideDuration: 6000,
              action: (key) => (
                <Button 
                  size="small" 
                  style={{ color: '#fff' }}
                  onClick={() => {
                    navigate('/bulk-upload-logs');
                  }}
                >
                  View Logs
                </Button>
              ),
            }
          );
          setPollingJobId(null);
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('Error checking job status:', error);
      return false;
    }
  };

  React.useEffect(() => {
    if (!pollingJobId) return;

    const pollInterval = setInterval(async () => {
      const completed = await checkJobStatus(pollingJobId);
      if (completed) {
        clearInterval(pollInterval);
      }
    }, 3000); // Poll every 3 seconds

    // Stop polling after 5 minutes
    const timeout = setTimeout(() => {
      clearInterval(pollInterval);
      setPollingJobId(null);
    }, 300000);

    return () => {
      clearInterval(pollInterval);
      clearTimeout(timeout);
    };
  }, [pollingJobId]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const validExtensions = ['.xlsx', '.xls', '.csv'];
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      
      if (!validExtensions.includes(fileExtension)) {
        enqueueSnackbar('Please select a valid Excel or CSV file', { variant: 'error' });
        return;
      }
      
      if (file.size > 50 * 1024 * 1024) {
        enqueueSnackbar('File size should not exceed 50MB', { variant: 'error' });
        return;
      }
      
      setSelectedFile(file);
    }
  };

  const handleDownloadTemplate = () => {
    try {
      const templateUrl = `${process.env.PUBLIC_URL}/templates/${moduleName}-template.csv`;
      const link = document.createElement('a');
      link.href = templateUrl;
      link.download = `${moduleName}-template.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      enqueueSnackbar('Template download started', { variant: 'success' });
    } catch (error) {
      console.error('Template download error:', error);
      enqueueSnackbar('Failed to download template', { variant: 'error' });
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      enqueueSnackbar('Please select a file first', { variant: 'warning' });
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('module_name', moduleName);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASEURL}bulk-upload`,
        formData,
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          },
        }
      );

      if (response.data?.status) {
        const jobId = response.data?.jobId;
        
        enqueueSnackbar(
          'File accepted for processing. You will be notified once complete.',
          { 
            variant: 'info',
            autoHideDuration: 4000,
            action: (key) => (
              <Button 
                size="small" 
                style={{ color: '#fff' }}
                onClick={() => {
                  navigate('/bulk-upload-logs');
                }}
              >
                View Logs
              </Button>
            ),
          }
        );
        
        // Start polling for job status
        if (jobId) {
          setPollingJobId(jobId);
        }
        
        setSelectedFile(null);
        if (onUploadSuccess) {
          onUploadSuccess(response.data);
        }
        onClose();
      } else {
        enqueueSnackbar(
          response.data?.message || 'Failed to upload',
          { 
            variant: 'error',
            action: (key) => (
              <Button 
                size="small" 
                style={{ color: '#fff' }}
                onClick={() => {
                  navigate('/bulk-upload-logs');
                }}
              >
                View Logs
              </Button>
            ),
          }
        );
      }
    } catch (error) {
      console.error('Upload error:', error);
      enqueueSnackbar(
        error.response?.data?.message || 'Failed to upload file',
        { 
          variant: 'error',
          action: (key) => (
            <Button 
              size="small" 
              style={{ color: '#fff' }}
              onClick={() => {
                navigate('/bulk-upload-logs');
              }}
            >
              View Logs
            </Button>
          ),
        }
      );
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleClose = () => {
    if (!uploading) {
      setSelectedFile(null);
      setUploadProgress(0);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Bulk Upload</Typography>
          <IconButton onClick={handleClose} disabled={uploading}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        <Box mb={3}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Download the template, fill in your data, and upload the file. The system will process it in the background.
          </Alert>
          
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadTemplate}
            fullWidth
            sx={{ 
              mb: 3,
              color: sidenavColor === 'info' ? '#1976d2' : sidenavColor === 'error' ? '#d32f2f' : '#d32f2f',
              borderColor: sidenavColor === 'info' ? '#1976d2' : sidenavColor === 'error' ? '#d32f2f' : '#d32f2f',
              '&:hover': {
                borderColor: sidenavColor === 'info' ? '#1565c0' : sidenavColor === 'error' ? '#c62828' : '#c62828',
                backgroundColor: 'rgba(211, 47, 47, 0.04)'
              }
            }}
          >
            Download Template
          </Button>
        </Box>

        <Box>
          <input
            accept=".xlsx,.xls,.csv"
            style={{ display: 'none' }}
            id="bulk-upload-file"
            type="file"
            onChange={handleFileChange}
            disabled={uploading}
          />
          <label htmlFor="bulk-upload-file">
            <Button
              variant="contained"
              component="span"
              startIcon={<CloudUploadIcon />}
              fullWidth
              disabled={uploading}
              sx={{
                backgroundColor: sidenavColor === 'info' ? '#1976d2' : sidenavColor === 'error' ? '#d32f2f' : '#d32f2f',
                color: "#FFF",
                '&:hover': {
                  backgroundColor: sidenavColor === 'info' ? '#1565c0' : sidenavColor === 'error' ? '#c62828' : '#c62828',
                }
              }}
            >
              Select File
            </Button>
          </label>
          
          {selectedFile && (
            <Box mt={2}>
              <Typography variant="body2" color="textSecondary">
                Selected: {selectedFile.name}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Size: {(selectedFile.size / 1024).toFixed(2)} KB
              </Typography>
            </Box>
          )}
          
          {uploading && (
            <Box mt={2}>
              <LinearProgress 
                variant="determinate" 
                value={uploadProgress}
                sx={{
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: sidenavColor === 'info' ? '#1976d2' : sidenavColor === 'error' ? '#d32f2f' : '#d32f2f',
                  }
                }}
              />
              <Typography variant="caption" color="textSecondary" align="center" display="block" mt={1}>
                Uploading: {uploadProgress}%
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={uploading}>
          Cancel
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={!selectedFile || uploading}
          sx={{
            backgroundColor: sidenavColor === 'info' ? '#1976d2' : sidenavColor === 'error' ? '#d32f2f' : '#d32f2f',
            color: "#FFF",
            '&:hover': {
              backgroundColor: sidenavColor === 'info' ? '#1565c0' : sidenavColor === 'error' ? '#c62828' : '#c62828',
            }
          }}
        >
          Upload
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkUploadDialog;
